import ApexCharts from "apexcharts";
import ApexOptions = ApexCharts.ApexOptions;
import {ChartData} from "../bo/chart_data";

export interface ChartOptions {
    chartData: ChartData,
    seriesColours: string[],
    height: string | number,
    showLegend: boolean,
    onlyLines?: boolean,
    noYAxisTitles?: boolean
}

function numberFormatterFactory(decimals: number) {
    return (val: number) => val != null ? val.toFixed(decimals) : ""
}

export function createAjaxOptions(params: ChartOptions) : ApexOptions {

    const chartData = params.chartData;
    console.log("Chart data", chartData);

    const ss = chartData.series || [];

    const series: ApexAxisChartSeries = [];

    const axisUnitTypes: { [key: string]: number[] | undefined } = {};

    const yaxes: ApexYAxis[] = [];

    const axisNames: { [key: number]: string[] } = {};
    const axisUnits: { [key: number]: string | undefined } = {};

    for (const s of ss) {

        let seriesName = s.title || "";
        let axisName = s.axisName || "";

        let values: number[] = [...(s.values || [])]; // clones array

        if (s.cummulative) {
            values = [];
            let acc = 0;
            for (const val of s.values || []) {
                acc = acc + val;
                values.push(acc);
            }
        }

        if (chartData.normalise && values.length > 0) {
            let acc = 0;
            for (const val of values) {
                acc += val;
            }
            const avg = acc / values.length;
            for (let i = 0; i < values.length; i++) {
                values[i] /= avg;
            }
        }

        let yaxisDecimals = 2;
        let ymin = 0;
        let ymax = 20;
        const nonnulls = values.filter((x) => x != null);
        if (nonnulls.length > 0) {
            const max = Math.max(...nonnulls);
            ymax = max * 1.05;
            ymin = Math.min(0,...nonnulls);
            const range = ymax - ymin;
            if (range > 10) {
                yaxisDecimals = 0;
            }
        }

        const labelFormatter = numberFormatterFactory(yaxisDecimals);

        const ser = {
            "name": seriesName,
            "data": values,
            "type": (s.lines || params.onlyLines) ? "line" : "column"
        };
        series.push(ser);

        const axisUnitType = s.axisType + "/" + s.axisUnits;

        const yaxis: ApexYAxis = {
            seriesName: seriesName,
            tickAmount: 5,
            min: ymin,
            max: ymax
        };

        if (axisUnitTypes[axisUnitType] === undefined) {
            const axis = yaxes.length;
            axisUnitTypes[axisUnitType] = [ axis ];
            yaxis.show = true;
            yaxis.opposite = Object.keys(axisUnitTypes).length % 2 == 0;
            yaxis.labels = {
                show: true,
                formatter: labelFormatter
            };

            axisNames[axis] = [axisName];
            axisUnits[axis] = s.axisUnits;

        } else {
            const sharedAxes = axisUnitTypes[axisUnitType] as number[];
            const sharedAxis = sharedAxes[0];

            const sharedYAxis = yaxes[sharedAxis];

            const min = Math.min(sharedYAxis.min as number, ymin);
            const max = Math.max(sharedYAxis.max as number, ymax);

            yaxis.show = false;
            for (const idx of sharedAxes) {
                yaxes[idx].min = min;
                yaxes[idx].max = max;
            }
            yaxis.min = min;
            yaxis.max = max;

            if (!axisNames[sharedAxis] || axisNames[sharedAxis].indexOf(axisName) === -1) {
                axisNames[sharedAxis].push(axisName);
            }

            sharedAxes.push(yaxes.length);
        }

        yaxes.push(yaxis);
    }

    let positive = false;
    let negative = false;
    for (const yaxis of yaxes) {
        // @ts-ignore
        if (yaxis.max !== undefined && yaxis.max > 0) {
            positive = true;
        }
        // @ts-ignore
        if (yaxis.min !== undefined && yaxis.min < 0) {
            negative = true;
        }
    }
    if (positive && negative) {
        for (const yaxis of yaxes) {
            let max = yaxis.max as number;
            let min = yaxis.min as number;
            if (max !== undefined && min !== undefined) {
                const l = Math.max(Math.abs(max), Math.abs(min));
                yaxis.min = -l;
                yaxis.max = l;
            }
        }
    }

    if (! params.noYAxisTitles) {
        for (const sharedAxis in axisNames) {
            const units = axisUnits[sharedAxis];
            const name = axisNames[sharedAxis].join(", ") + (units ? ` (${units})` : "");
            yaxes[sharedAxis].title = {
                text: name
            }
        }
    }

    // ---------------------------------------------------------------
    // Bugzilla #2392: Workaround bug in ApexCharts: put series in same order as yaxes

    const seriesMap: any = {};
    series.map((s) => {
        seriesMap[s.name || ""] = s;
    });

    const seriesBugfix: ApexAxisChartSeries = [];

    for (const yaxis of yaxes) {
        const seriesName = yaxis.seriesName as string;
        const s = seriesMap[seriesName];
        seriesBugfix.push(s);
        delete seriesMap[seriesName];
    }

    for (const s of series) {
        const seriesName = s.name as string;
        if (seriesMap[seriesName]) {
            seriesBugfix.push(s);
        }
        delete seriesMap[seriesName];
    }

    // Bugzilla #2392: End workaround bug in ApexCharts
    // ---------------------------------------------------------------

    const dataLabels: ApexDataLabels = chartData.labels ? {
        enabled: true,
        style: {
            fontSize: "10px",
            colors: ["#545454"]
        },
        background: {
            enabled: false,
            dropShadow: {
                enabled: false
            }
        },
        offsetY: -20,
        formatter: (val: number, opts: any) => val !== null ? val.toFixed(2) : ""
    } : {
        enabled: false
    };

    const tooltipFormatter = numberFormatterFactory(2);

    const opts: ApexOptions = {
        chart: {
            height: params.height,
            zoom: {
                enabled: true
            },
            toolbar: {
                show: false
            }
        },
        legend: {
            show: params.showLegend,
            position: "top",
            horizontalAlign: "right"
        },
        series: seriesBugfix,
        colors: params.seriesColours,
        dataLabels: dataLabels,
        stroke: {
            curve: 'straight',
            width: 3
        },
        grid: {
            row: {
                colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
                opacity: 0.5
            },
        },
        xaxis: {
            categories: chartData.ticks,
            labels: {
                rotate: -45,
                rotateAlways: true
            }
        },
        yaxis: yaxes,
        tooltip: {
            y: {
                formatter: tooltipFormatter
            }
        }
    };

    if (chartData.normalise) {
        (opts.yaxis as ApexYAxis).title = {
            text: "Normalised: 100 = series average"
        };
    }

    return opts;
}

type GcCallback = () => void;

interface ChartPair {
    container: HTMLElement;
    chart: ApexCharts;
    callback: GcCallback | undefined;
}

const rendered : ChartPair[] = [];

export function renderDisposably(container: HTMLElement, opts: ApexOptions, onGC?: GcCallback) {

    function gc() {
        for (let i = rendered.length - 1; i >= 0; i--) {
            const r = rendered[i];
            const elt = r.container;
            const keep = document.body.contains(elt);
            if (! keep) {
                // Garbage collect!
                try {
                    r.chart.destroy();
                } catch (e) {
                    // continue - don't let something weird in apexcharts stop us here.
                    console.log("Failed to destroy chart, continuing", e);
                }

                r.callback && r.callback();
                rendered.splice(i, 1);
            }
        }
    }

    gc();

    const chart = new ApexCharts(container, opts);

    rendered.push({
        container: container,
        chart: chart,
        callback: onGC
    });

    const render_ = chart.render();
    return render_;
}


